import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Scissors, Leaf, Globe, Heart, Zap, Users } from 'lucide-react';
import Header from './components/Header25';
import Footer from './components/Footer25';

const About25 = () => {
    const navigate = useNavigate();

    const values = [
        { icon: Scissors, title: 'Craftsmanship', desc: 'Every piece is crafted with meticulous attention to detail, honoring generations of textile artistry.' },
        { icon: Leaf, title: 'Sustainability', desc: 'We source responsibly and minimize waste, ensuring fashion that respects the planet.' },
        { icon: Globe, title: 'Contemporary Vision', desc: 'Traditional Indian aesthetics reimagined through a modern, global design lens.' },
        { icon: Heart, title: 'Authenticity', desc: 'Genuine materials, honest pricing, and transparent processes at every step.' },
        { icon: Zap, title: 'Innovation', desc: 'Pushing boundaries with new silhouettes, unexpected textures, and bold combinations.' },
        { icon: Users, title: 'Community', desc: 'Building a network of artisans, designers, and patrons who share our vision.' },
    ];

    const milestones = [
        { year: '2019', title: 'Founded', desc: 'Started with a small studio in Mumbai with a vision for contemporary Indian fashion.' },
        { year: '2020', title: 'First Collection', desc: 'Launched "Roots & Routes" — 24 pieces that blended heritage weaves with modern cuts.' },
        { year: '2022', title: 'Digital First', desc: 'Expanded online, reaching customers across India and internationally.' },
        { year: '2024', title: 'Artisan Network', desc: 'Partnered with 50+ artisan clusters across 8 Indian states.' },
        { year: '2026', title: 'Today', desc: 'Continuing to redefine what contemporary Indian fashion means globally.' },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
                .sahaj-about * { box-sizing: border-box; }
                .sahaj-about { font-family: 'IBM Plex Sans', sans-serif; background: #FFFFFF; color: #111111; }
                .sahaj-about-heading { font-family: 'Space Grotesk', sans-serif; }
            `}</style>
            <div className="sahaj-about" style={{ minHeight: '100vh' }}>
                <Header />

                {/* Hero */}
                <div style={{ background: '#111111', marginTop: 80, padding: '100px 0', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
                        <p style={{
                            fontSize: 13, fontWeight: 600, color: '#0D7C66',
                            textTransform: 'uppercase', letterSpacing: '3px', marginBottom: 24
                        }}>
                            Our Story
                        </p>
                        <h1 className="sahaj-about-heading" style={{
                            fontSize: 72, fontWeight: 700, color: '#FFFFFF', lineHeight: 1,
                            letterSpacing: '-2px', margin: '0 0 32px', maxWidth: 800
                        }}>
                            FASHION THAT SPEAKS THE LANGUAGE OF NOW
                        </h1>
                        <div style={{ width: 80, height: 4, background: '#0D7C66', marginBottom: 32 }} />
                        <p style={{
                            fontSize: 20, color: '#9CA3AF', maxWidth: 600, lineHeight: 1.6, fontWeight: 300
                        }}>
                            We believe Indian fashion deserves a contemporary voice. Clean lines. Bold choices. Zero compromise on craft.
                        </p>
                    </div>
                </div>

                {/* Brand Manifesto */}
                <div style={{ maxWidth: 1280, margin: '0 auto', padding: '100px 24px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
                        <div>
                            <h2 className="sahaj-about-heading" style={{
                                fontSize: 48, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-1px',
                                margin: '0 0 32px'
                            }}>
                                WE DON'T FOLLOW TRENDS. WE SET THE STANDARD.
                            </h2>
                            <p style={{ fontSize: 17, lineHeight: 1.8, color: '#6C757D', marginBottom: 24 }}>
                                Born from a deep respect for Indian textile heritage and a restless desire for the new, our brand exists at the intersection of tradition and tomorrow. Every kurta, every saree, every silhouette is designed to make you feel both rooted and free.
                            </p>
                            <p style={{ fontSize: 17, lineHeight: 1.8, color: '#6C757D', marginBottom: 32 }}>
                                We work directly with artisan communities across India, ensuring fair practices while bringing world-class design to every piece. This is fashion with purpose.
                            </p>
                            <button
                                onClick={() => navigate('/template25/shop')}
                                style={{
                                    padding: '14px 32px', background: '#0D7C66', color: '#FFFFFF',
                                    border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                                    display: 'inline-flex', alignItems: 'center', gap: 10,
                                    textTransform: 'uppercase', letterSpacing: '2px',
                                    fontFamily: 'Space Grotesk, sans-serif',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={e => e.target.style.background = '#0A9B7F'}
                                onMouseLeave={e => e.target.style.background = '#0D7C66'}
                            >
                                Explore Collection
                                <ArrowRight size={18} />
                            </button>
                        </div>
                        <div style={{ aspectRatio: '4/5', overflow: 'hidden' }}>
                            <img
                                src="https://images.pexels.com/photos/6311652/pexels-photo-6311652.jpeg?auto=compress&cs=tinysrgb&w=800"
                                alt="Craftsmanship"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Values Grid */}
                <div style={{ background: '#F8F9FA', padding: '100px 0' }}>
                    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
                        <h2 className="sahaj-about-heading" style={{
                            fontSize: 40, fontWeight: 700, textAlign: 'center', marginBottom: 16,
                            letterSpacing: '-1px'
                        }}>
                            WHAT WE STAND FOR
                        </h2>
                        <p style={{ fontSize: 16, color: '#6C757D', textAlign: 'center', marginBottom: 60, maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' }}>
                            Our values shape every stitch, every decision, every interaction
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
                            {values.map((val, idx) => (
                                <div key={idx} style={{
                                    padding: 40, background: '#FFFFFF', border: '1px solid #DEE2E6',
                                    transition: 'border-color 0.2s, box-shadow 0.2s'
                                }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#0D7C66'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.06)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#DEE2E6'; e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                    <div style={{
                                        width: 56, height: 56, background: '#E8F5F1', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', marginBottom: 24
                                    }}>
                                        <val.icon size={26} color="#0D7C66" />
                                    </div>
                                    <h3 className="sahaj-about-heading" style={{
                                        fontSize: 20, fontWeight: 700, margin: '0 0 12px'
                                    }}>
                                        {val.title}
                                    </h3>
                                    <p style={{ fontSize: 14, lineHeight: 1.7, color: '#6C757D', margin: 0 }}>
                                        {val.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Timeline */}
                <div style={{ maxWidth: 800, margin: '0 auto', padding: '100px 24px' }}>
                    <h2 className="sahaj-about-heading" style={{
                        fontSize: 40, fontWeight: 700, textAlign: 'center', marginBottom: 60,
                        letterSpacing: '-1px'
                    }}>
                        THE JOURNEY
                    </h2>
                    {milestones.map((ms, idx) => (
                        <div key={idx} style={{
                            display: 'flex', gap: 32, paddingBottom: 40,
                            marginBottom: idx < milestones.length - 1 ? 40 : 0,
                            borderBottom: idx < milestones.length - 1 ? '1px solid #DEE2E6' : 'none'
                        }}>
                            <div style={{ flexShrink: 0, width: 80 }}>
                                <span className="sahaj-about-heading" style={{
                                    fontSize: 32, fontWeight: 700, color: '#0D7C66'
                                }}>
                                    {ms.year}
                                </span>
                            </div>
                            <div>
                                <h3 className="sahaj-about-heading" style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>
                                    {ms.title}
                                </h3>
                                <p style={{ fontSize: 15, lineHeight: 1.6, color: '#6C757D', margin: 0 }}>
                                    {ms.desc}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* CTA Banner */}
                <div style={{ background: '#111111', padding: '80px 0' }}>
                    <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
                        <h2 className="sahaj-about-heading" style={{
                            fontSize: 40, fontWeight: 700, color: '#FFFFFF', marginBottom: 24,
                            letterSpacing: '-1px'
                        }}>
                            READY TO EXPERIENCE THE DIFFERENCE?
                        </h2>
                        <p style={{ fontSize: 16, color: '#9CA3AF', marginBottom: 40 }}>
                            Explore our collection of contemporary Indian wear
                        </p>
                        <button
                            onClick={() => navigate('/template25/shop')}
                            style={{
                                padding: '16px 48px', background: '#0D7C66', color: '#FFFFFF',
                                border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 10,
                                textTransform: 'uppercase', letterSpacing: '2px',
                                fontFamily: 'Space Grotesk, sans-serif',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={e => e.target.style.background = '#0A9B7F'}
                            onMouseLeave={e => e.target.style.background = '#0D7C66'}
                        >
                            Shop Now
                            <ArrowRight size={18} />
                        </button>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default About25;
